import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router';
import {
    Container,
    Typography,
    Box,
    CircularProgress,
    Alert,
    Breadcrumbs,
    Link,
    Grid,
    Card,
    CardContent,
    CardActions,
    Button,
    Chip,
    Divider,
    LinearProgress,
} from '@mui/material';
import {
    Home as HomeIcon,
    School as SchoolIcon,
    Topic as TopicIcon,
    PlayArrow as PlayIcon,
    Refresh as RetryIcon,
    CheckCircle as CompleteIcon,
    Assignment as QuizIcon,
    Lock as LockIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CelebrationIcon from '@mui/icons-material/Celebration';
import {createApiClient} from '../../services/ApiClient.jsx';
import {useTopicProgress} from './hooks/useTopicProgress.js';
import Quantino from '../../components/Quantino/Quantino.jsx';

// Helper function to format score nicely
const formatScore = (score) => {
    if (score % 1 === 0) {
        // If it's a whole number, return as integer
        return score.toString();
    } else if (score % 0.5 === 0) {
        // If it's a half number (like 1.5), show one decimal
        return score.toFixed(1);
    } else {
        // For other decimals, round to 2 decimal places
        return (Math.round(score * 100) / 100).toString();
    }
};

const TopicCard = ({topic, courseId, onStartTopic, onViewHistory, progress}) => {
    const totalAttempts = progress?.totalAttempts || 0;
    const hasCompletedSubmission = progress?.hasCompletedSubmission || false;
    const bestScore = progress?.bestScore || 0;

    // Determine card state
    const hasQuiz = topic.has_form && topic.question_count > 0;
    const isDisabled = !hasQuiz;
    const hasAttempts = totalAttempts > 0;

    // Button text and icon logic
    const getButtonProps = () => {
        if (isDisabled) {
            return {
                text: 'No Quiz Available',
                icon: <LockIcon/>,
                variant: 'outlined',
                disabled: true
            };
        }

        if (hasAttempts) {
            return {
                text: hasCompletedSubmission ? 'Try Again' : 'Continue',
                icon: <RetryIcon/>,
                variant: hasCompletedSubmission ? 'outlined' : 'contained',
                disabled: false
            };
        }

        return {
            text: 'Start Topic',
            icon: <PlayIcon/>,
            variant: 'contained',
            disabled: false
        };
    };

    const buttonProps = getButtonProps();

    return (
        <Card
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: 3,
                transition: 'transform 0.25s, box-shadow 0.25s',
                width:'480px',
                opacity: isDisabled ? 0.7 : 1,
                backgroundColor: 'background.paper',
                border: `2px solid ${hasCompletedSubmission ? 'rgba(0,191,174,0.2)' : 'rgba(41,98,255,0.15)'}`,
                '&:hover': !isDisabled ? {
                    transform: 'scale(1.02) translateY(-2px)',
                    boxShadow: 6,
                    border: `2px solid ${hasCompletedSubmission ? 'rgba(0,191,174,0.4)' : 'rgba(41,98,255,0.3)'}`,
                } : {},
            }}
        >
            <CardContent sx={{flexGrow: 1}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        <EmojiEventsIcon
                            sx={{
                                fontSize: 28,
                                color: hasCompletedSubmission ? 'secondary.main' : 'primary.main'
                            }}
                        />
                        <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                                fontWeight: 'bold',
                                color: 'text.primary',
                                mb: 0
                            }}
                        >
                            {topic.name}
                        </Typography>
                    </Box>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                        {hasCompletedSubmission && (
                            <CelebrationIcon
                                sx={{
                                    color: 'secondary.main',
                                    fontSize: 28,
                                    animation: 'bounce 1s ease-in-out infinite alternate'
                                }}
                            />
                        )}
                        {isDisabled && (
                            <LockIcon sx={{color: 'text.disabled'}}/>
                        )}
                    </Box>
                </Box>

                <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{mb: 2, minHeight: '40px', lineHeight: 1.4}}
                >
                    {topic.description || 'No description available'}
                </Typography>

                <Box sx={{display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap'}}>
                    <Chip
                        icon={<QuizIcon/>}
                        label={`${topic.question_count || 0} Questions`}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{fontWeight: 'medium'}}
                    />
                    <Chip
                        icon={<TrendingUpIcon/>}
                        label={`Best ${formatScore(bestScore)}%`}
                        size="small"
                        variant="outlined"
                        color={hasCompletedSubmission ? "secondary" : "primary"}
                        sx={{fontWeight: 'medium'}}
                    />
                    {/* Show attempts count when user has attempts */}
                    {hasAttempts && (
                        <Chip
                            icon={<HistoryIcon/>}
                            label={`${totalAttempts} attempt${totalAttempts === 1 ? '' : 's'}`}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{fontWeight: 'medium'}}
                        />
                    )}
                    {hasCompletedSubmission && (
                        <Chip
                            icon={<CompleteIcon/>}
                            label="Completed"
                            size="small"
                            color="secondary"
                            variant="filled"
                        />
                    )}
                </Box>

                {/* Progress bar with better colors */}
                <Box sx={{mb: 2}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="caption" color="text.secondary" fontWeight="medium">
                            Progress
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {formatScore(bestScore)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={bestScore}
                        sx={{
                            height: 8,
                            borderRadius: 4,
                            backgroundColor: 'divider',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 4,
                                background: 'linear-gradient(90deg, #FF9800, #4CAF50)',
                            },
                        }}
                    />
                </Box>
            </CardContent>

            <CardActions sx={{p: 2, pt: 0}}>
                {/* Main action button */}
                <Button
                    variant={buttonProps.variant}
                    fullWidth={!hasAttempts} // Only full width if no attempts (no history button)
                    onClick={() => !isDisabled && onStartTopic(topic.id)}
                    disabled={buttonProps.disabled}
                    startIcon={buttonProps.icon}
                    color={hasCompletedSubmission ? "secondary" : "primary"}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        py: 1.5,
                        flex: hasAttempts ? 1 : undefined, // Take remaining space when history button exists
                    }}
                >
                    {buttonProps.text}
                </Button>

                {/* History button - only show when user has attempts */}
                {hasAttempts && (
                    <Button
                        variant="outlined"
                        onClick={() => onViewHistory(topic.id)}
                        startIcon={<HistoryIcon/>}
                        color="info"
                        sx={{
                            textTransform: 'none',
                            fontWeight: 'bold',
                            py: 1.5,
                            px: 2,
                            minWidth: 'auto',
                            ml: 1,
                        }}
                        title="View submission history"
                    >
                        History
                    </Button>
                )}
            </CardActions>
        </Card>
    );
};

const StudentCoursePage = ({accessToken, claims}) => {
    const {courseId} = useParams();
    const navigate = useNavigate();

    const [course, setCourse] = useState(null);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [quantino, setQuantino] = useState({
        mood: 'neutral',
        message: '',
        isVisible: false,
        isBubbleVisible: false,
    });

    const apiClient = createApiClient(accessToken);
    const username = claims?.username || claims?.name || 'Student';

    // Use the topic progress hook
    const {topicProgress, loading: progressLoading} = useTopicProgress(accessToken, topics);

    const fetchCourse = async () => {
        try {
            const courseResponse = await apiClient.get(`/api/courses/${courseId}`);
            setCourse(courseResponse);
            setError(null);
        } catch (err) {
            console.error('Error fetching course:', err);

            // If we get a 403 error, try to join the course first
            if (err.response?.status === 403) {
                try {
                    console.log('Access denied, attempting to join course automatically...');
                    await apiClient.post(`/api/courses/${courseId}/join`);

                    // After joining, try to fetch the course again
                    const courseResponse = await apiClient.get(`/api/courses/${courseId}`);
                    setCourse(courseResponse);
                    setError(null);
                    return;
                } catch (joinErr) {
                    console.error('Error auto-joining course:', joinErr);
                    // If joining fails, show the original error
                }
            }

            setError('Failed to load course. Please try again later.');
        }
    };

    const fetchTopics = async () => {
        try {
            const topicsResponse = await apiClient.get(`/api/courses/${courseId}/topics`);
            const topicsList = topicsResponse.topics || topicsResponse || [];
            // Sort topics alphabetically by name
            const sortedTopics = topicsList.sort((a, b) =>
                a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
            );
            setTopics(sortedTopics);
            setError(null);
        } catch (err) {
            console.error('Error fetching topics:', err);

            // If we get a 403 error, try to join the course first
            if (err.response?.status === 403) {
                try {
                    console.log('Access denied for topics, attempting to join course automatically...');
                    await apiClient.post(`/api/courses/${courseId}/join`);

                    // After joining, try to fetch the topics again
                    const topicsResponse = await apiClient.get(`/api/courses/${courseId}/topics`);
                    const topicsList = topicsResponse.topics || topicsResponse || [];
                    // Sort topics alphabetically by name
                    const sortedTopics = topicsList.sort((a, b) =>
                        a.name.localeCompare(b.name, 'de', { sensitivity: 'base' })
                    );
                    setTopics(sortedTopics);
                    setError(null);
                    return;
                } catch (joinErr) {
                    console.error('Error auto-joining course for topics:', joinErr);
                    // If joining fails, show the original error
                }
            }

            setError('Failed to load course topics. Please try again later.');
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchCourse(), fetchTopics()]);
            } catch (err) {
                console.error('Error fetching course page data:', err);
                setError('Failed to load course data. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        if (accessToken && courseId) {
            fetchData();
        }
    }, [accessToken, courseId]);

    // Quantino welcome message
    useEffect(() => {
        if (course && topics.length > 0 && !loading) {
            const completedTopics = Object.values(topicProgress).filter(p => p?.hasCompletedSubmission).length;
            const totalTopics = topics.length;
            
            const timer = setTimeout(() => {
                // Define mood arrays for different scenarios
                const welcomeMoods = ['motivierend', 'freundlich', 'zwinkernd'];
                const progressMoods = ['stolz_daumen_hoch', 'freundlich', 'motivierend', 'überrascht'];
                const completedMoods = ['feiernd', 'stolz_daumen_hoch', 'überrascht'];
                const encouragingMoods = ['motivierend', 'nachdenklich', 'freundlich'];
                const concernedMoods = ['streng', 'nachdenklich', 'traurig']; // For poor performance
                const motivatingMoods = ['motivierend', 'freundlich', 'nachdenklich']; // For average performance
                
                // Calculate best scores and topic insights
                const topicScores = Object.entries(topicProgress).map(([topicId, progress]) => {
                    // Try both string and number comparison to handle ID type mismatches
                    const topic = topics.find(t => t.id === parseInt(topicId) || t.id === topicId || t.id.toString() === topicId);
                    return {
                        name: topic?.name || 'Unknown Topic',
                        score: Math.round(progress?.bestScore || 0), // Round scores
                        hasCompleted: progress?.hasCompletedSubmission || false,
                        attempts: progress?.attemptCount || 0
                    };
                }).filter(t => t.hasCompleted);

                const bestScore = topicScores.length > 0 ? Math.round(Math.max(...topicScores.map(t => t.score))) : 0;
                const averageScore = topicScores.length > 0 ? Math.round(topicScores.reduce((sum, t) => sum + t.score, 0) / topicScores.length) : 0;
                const highestScoringTopic = topicScores.find(t => t.score === bestScore);
                
                let mood, message;

                if (completedTopics === 0) {
                    mood = welcomeMoods[Math.floor(Math.random() * welcomeMoods.length)];
                    const topicNames = topics.slice(0, 3).map(t => t.title).join(', ');
                    const messages = [
                        `Welcome to ${course.title}! Ready to explore topics like ${topicNames}?`,
                        `Hello! ${course.title} has ${totalTopics} fascinating topics waiting for you!`,
                        `Time to dive in! Your journey through ${totalTopics} topics starts now!`,
                        `Ready to learn? ${course.title} offers ${totalTopics} topics to master!`
                    ];
                    message = messages[Math.floor(Math.random() * messages.length)];
                } else if (completedTopics === totalTopics) {
                    // Check performance when all topics are completed
                    if (averageScore >= 85) {
                        mood = completedMoods[Math.floor(Math.random() * completedMoods.length)];
                        const messages = [
                            `Amazing! All ${totalTopics} topics mastered with an excellent ${averageScore}% average!`,
                            `Incredible! ${course.title} complete with outstanding ${bestScore}% best score!`,
                            `Outstanding! You've conquered all topics with ${averageScore}% average!`,
                            `Brilliant! Perfect mastery with ${bestScore}% best score!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    } else if (averageScore >= 70) {
                        mood = progressMoods[Math.floor(Math.random() * progressMoods.length)];
                        const messages = [
                            `Great job! All ${totalTopics} topics completed with ${averageScore}% average!`,
                            `Well done! ${course.title} finished with solid ${averageScore}% average!`,
                            `Good work! You've completed all topics with ${averageScore}% average!`,
                            `Nice! ${course.title} complete - best score was ${bestScore}%!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    } else if (averageScore >= 50) {
                        mood = motivatingMoods[Math.floor(Math.random() * motivatingMoods.length)];
                        const messages = [
                            `Course complete! ${averageScore}% average shows effort, but let's aim higher next time!`,
                            `All topics done! ${averageScore}% average - there's room for improvement!`,
                            `${course.title} finished! ${averageScore}% average suggests more practice needed!`,
                            `Complete! Best score ${bestScore}% - let's work on improving those results!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    } else {
                        mood = concernedMoods[Math.floor(Math.random() * concernedMoods.length)];
                        const messages = [
                            `Course complete, but ${averageScore}% average is concerning. Time to review!`,
                            `All topics attempted, but ${averageScore}% shows major gaps. Let's study more!`,
                            `${course.title} finished with ${averageScore}% - serious review needed!`,
                            `Complete, but ${bestScore}% best score suggests fundamental understanding issues!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    }
                } else {
                    const progressPercentage = Math.round((completedTopics / totalTopics) * 100);
                    
                    // Excellent performance (85%+)
                    if (averageScore >= 85 && completedTopics >= 2) {
                        mood = progressMoods[Math.floor(Math.random() * progressMoods.length)];
                        const messages = [
                            `Excellent work! ${completedTopics}/${totalTopics} topics with ${averageScore}% average!`,
                            highestScoringTopic ? `Outstanding! Your best topic "${highestScoringTopic.name}" scored ${bestScore}%!` : `Amazing progress! ${progressPercentage}% done with ${averageScore}% average!`,
                            `You're excelling! ${completedTopics} topics mastered with ${averageScore}% average!`,
                            `Incredible performance! ${progressPercentage}% through with ${bestScore}% best score!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    }
                    // Good performance (80-84%)
                    else if (averageScore >= 80 && completedTopics >= 2) {
                        mood = progressMoods[Math.floor(Math.random() * progressMoods.length)];
                        const messages = [
                            `Great job! ${completedTopics}/${totalTopics} topics with solid ${averageScore}% average!`,
                            highestScoringTopic ? `Well done! Best topic "${highestScoringTopic.name}" scored ${bestScore}%!` : `Good progress! ${progressPercentage}% through with ${averageScore}% average!`,
                            `You're doing well! ${completedTopics} topics with ${averageScore}% average!`,
                            `Nice work! ${progressPercentage}% complete, best score ${bestScore}%!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    }
                    // Average performance (60-79%)
                    else if (averageScore >= 60 && completedTopics >= 2) {
                        mood = motivatingMoods[Math.floor(Math.random() * motivatingMoods.length)];
                        const messages = [
                            `Keep pushing! ${completedTopics}/${totalTopics} topics with ${averageScore}% average - room to grow!`,
                            `You're making progress! ${averageScore}% average shows effort, let's aim higher!`,
                            `Good effort! ${completedTopics} topics done, let's work on improving that ${averageScore}%!`,
                            `${progressPercentage}% through - your ${averageScore}% average can definitely improve!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    }
                    // Poor performance (50-59%)
                    else if (averageScore >= 50 && completedTopics >= 2) {
                        mood = concernedMoods[Math.floor(Math.random() * concernedMoods.length)];
                        const messages = [
                            `${averageScore}% average needs improvement. Let's focus and try harder!`,
                            `I see ${completedTopics} topics done, but ${averageScore}% suggests we need more practice!`,
                            `${averageScore}% average is concerning. Time to review and strengthen your understanding!`,
                            `You've tried ${completedTopics} topics, but ${averageScore}% shows we need better focus!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    }
                    // Very poor performance (<50%)
                    else if (averageScore < 50 && completedTopics >= 2) {
                        mood = concernedMoods[Math.floor(Math.random() * concernedMoods.length)];
                        const messages = [
                            `${averageScore}% average is quite low. We need to seriously review the material!`,
                            `${completedTopics} topics completed, but ${averageScore}% suggests major gaps in understanding!`,
                            `This ${averageScore}% average is worrying. Let's go back to basics and study more!`,
                            `${averageScore}% shows we're struggling. Time for intensive review and practice!`
                        ];
                        message = messages[Math.floor(Math.random() * messages.length)];
                    }
                    // First topic completed
                    else if (completedTopics === 1) {
                        const firstTopic = topicScores[0];
                        const roundedScore = Math.round(firstTopic.score);
                        if (roundedScore >= 80) {
                            mood = encouragingMoods[Math.floor(Math.random() * encouragingMoods.length)];
                            message = `Great start! First topic "${firstTopic.name}" scored ${roundedScore}%!`;
                        } else if (roundedScore >= 60) {
                            mood = motivatingMoods[Math.floor(Math.random() * motivatingMoods.length)];
                            message = `Decent start! ${roundedScore}% on "${firstTopic.name}" - let's aim higher next time!`;
                        } else if (roundedScore >= 40) {
                            mood = concernedMoods[Math.floor(Math.random() * concernedMoods.length)];
                            message = `${roundedScore}% on "${firstTopic.name}" needs improvement. Let's review and try again!`;
                        } else {
                            mood = concernedMoods[Math.floor(Math.random() * concernedMoods.length)];
                            message = `${roundedScore}% on "${firstTopic.name}" is quite concerning. We need to study harder!`;
                        }
                    }
                    // No completed topics yet
                    else {
                        mood = welcomeMoods[Math.floor(Math.random() * welcomeMoods.length)];
                        const nextTopics = topics.filter(t => !Object.keys(topicProgress).includes(t.id.toString())).slice(0, 2);
                        const nextTopicNames = nextTopics.map(t => t.name).join('" and "');
                        
                        if (nextTopics.length > 0 && nextTopicNames.trim()) {
                            const messages = [
                                `Ready to start? Try "${nextTopicNames}" first!`,
                                `Let's begin! "${nextTopicNames}" are waiting for you!`,
                                `Time to dive in! How about starting with "${nextTopicNames}"?`,
                                `Your journey awaits! "${nextTopicNames}" look interesting!`
                            ];
                            message = messages[Math.floor(Math.random() * messages.length)];
                        } else {
                            // Fallback messages when no specific topics are found
                            const fallbackMessages = [
                                `Welcome to ${course.title}! Ready to start learning?`,
                                `Let's begin your learning journey!`,
                                `Time to explore and learn something new!`,
                                `Your adventure in ${course.title} awaits!`
                            ];
                            message = fallbackMessages[Math.floor(Math.random() * fallbackMessages.length)];
                        }
                    }
                }

                setQuantino({
                    mood,
                    message,
                    isVisible: true,
                    isBubbleVisible: true,
                });

                // Auto-hide bubble after 8 seconds, then keep Quantino visible but smaller
                const hideBubbleTimer = setTimeout(() => {
                    setQuantino(prev => ({...prev, isBubbleVisible: false}));
                    
                    // Keep Quantino visible but smaller after bubble disappears
                    const minimizeTimer = setTimeout(() => {
                        setQuantino(prev => ({...prev, isVisible: true})); // Keep visible, just smaller
                    }, 2000);

                    return () => clearTimeout(minimizeTimer);
                }, 8000);

                return () => clearTimeout(hideBubbleTimer);
            }, 1000);

            return () => clearTimeout(timer);
        }
    }, [course, topics, topicProgress, loading]);

    const handleStartTopic = (topicId) => {
        // Navigate to topic page where students can answer questions
        navigate(`/student/course/${courseId}/topic/${topicId}`);
    };

    const handleViewHistory = (topicId) => {
        // Navigate to topic page but with a URL parameter to show history
        // Add a source parameter to indicate where we came from
        navigate(`/student/course/${courseId}/topic/${topicId}?view=history&source=course`);
    };

    const handleBackToDashboard = () => {
        navigate('/student/dashboard');
    };

    if (loading) {
        return (
            <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
                <Box sx={{display: 'flex', justifyContent: 'center', my: 4}}>
                    <CircularProgress/>
                </Box>
            </Container>
        );
    }

    if (error) {
        return (
            <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
                <Alert severity="error" sx={{mb: 4}}>
                    {error}
                </Alert>
                <Button variant="outlined" onClick={handleBackToDashboard}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    if (!course) {
        return (
            <Container maxWidth="xl" sx={{mt: 4, mb: 8}}>
                <Alert severity="warning" sx={{mb: 4}}>
                    Course not found.
                </Alert>
                <Button variant="outlined" onClick={handleBackToDashboard}>
                    Back to Dashboard
                </Button>
            </Container>
        );
    }

    // Calculate course progress based on actual topic progress
    const completedTopics = Object.values(topicProgress).filter(p => p?.hasCompletedSubmission).length;
    const totalTopics = topics.length;
    const courseProgress = totalTopics > 0 ? (completedTopics / totalTopics) * 100 : 0;

    return (
        <Container maxWidth="xl" sx={{mb: 2, py: 2, pb: 5}}>
            {/* Quantino */}
            <Quantino
                mood={quantino.mood}
                message={quantino.message}
                isVisible={quantino.isVisible}
                isBubbleVisible={quantino.isBubbleVisible}
            />
            
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{mb: 2}}>
                <Link
                    color="inherit"
                    href="/student/dashboard"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <HomeIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    Dashboard
                </Link>
                <Typography
                    color="text.primary"
                    sx={{display: 'flex', alignItems: 'center'}}
                >
                    <SchoolIcon sx={{mr: 0.5}} fontSize="inherit"/>
                    {course.title}
                </Typography>
            </Breadcrumbs>

            {/* Divider between breadcrumbs and header */}
            <Divider sx={{mb: 4}}/>

            {/* Course Header */}
            <Box sx={{mb: 4}}>
                <Typography variant="h4" gutterBottom>
                    {course.title}
                </Typography>

                {course.description && (
                    <Typography variant="subtitle1" color="text.secondary" sx={{mb: 3}}>
                        {course.description}
                    </Typography>
                )}

                {/* Course Progress */}
                <Box sx={{mb: 3}}>
                    <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1}}>
                        <Typography variant="h6">
                            Course Progress
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {completedTopics} of {totalTopics} topics completed
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={courseProgress}
                        sx={{
                            height: 12,
                            borderRadius: 6,
                            backgroundColor: 'grey.200',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 6,
                            },
                        }}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
                        {Math.round(courseProgress)}% Complete
                    </Typography>
                </Box>

                {/* Course Stats */}
                <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>
                    <Chip
                        icon={<TopicIcon/>}
                        label={`${totalTopics} Topics`}
                        variant="outlined"
                        size="medium"
                    />
                    <Chip
                        icon={<SchoolIcon/>}
                        label={`Teacher: ${course.teacher?.username || 'Unknown'}`}
                        variant="outlined"
                        size="medium"
                    />
                </Box>
            </Box>

            {/* Topics Section */}
            <Box sx={{mb: 4}}>
                <Typography variant="h5" gutterBottom sx={{display: 'flex', alignItems: 'center', mb: 2}}>
                    <TopicIcon sx={{mr: 1}}/>
                    Course Topics
                </Typography>

                {topics.length === 0 ? (
                    <Card>
                        <CardContent sx={{textAlign: 'center', py: 6}}>
                            <TopicIcon sx={{fontSize: 64, color: 'text.secondary', mb: 2}}/>
                            <Typography variant="h6" gutterBottom>
                                No Published Topics Yet
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Your teacher hasn't published any topics for this course yet. Topics need to be
                                published before you can access them.
                            </Typography>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        {progressLoading && (
                            <Box sx={{display: 'flex', justifyContent: 'center', mb: 2}}>
                                <CircularProgress size={24}/>
                                <Typography variant="body2" color="text.secondary" sx={{ml: 1}}>
                                    Loading progress data...
                                </Typography>
                            </Box>
                        )}
                        <Grid container spacing={3}>
                            {topics.map((topic) => (
                                <Grid item xs={12} sm={6} md={4} key={topic.id}>
                                    <TopicCard
                                        topic={topic}
                                        courseId={courseId}
                                        onStartTopic={handleStartTopic}
                                        onViewHistory={handleViewHistory}
                                        progress={topicProgress[topic.id]}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </>
                )}
            </Box>
        </Container>
    );
};

export default StudentCoursePage;
